import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/toaster";
import ChatWidget from "@/components/ChatWidget";
import AuthCallback from "@/pages/AuthCallback";

import Landing from "@/pages/public/Landing";
import PublicCases from "@/pages/public/PublicCases";
import PublicCaseDetail from "@/pages/public/PublicCaseDetail";
import PublicSanctuary from "@/pages/public/PublicSanctuary";
import SuccessStories from "@/pages/public/SuccessStories";
import Donate from "@/pages/public/Donate";
import Guardians from "@/pages/public/Guardians";
import GuardianPortal from "@/pages/public/GuardianPortal";
import Terms from "@/pages/public/legal/Terms";
import Privacy from "@/pages/public/legal/Privacy";
import RefundPolicy from "@/pages/public/legal/RefundPolicy";
import Contact from "@/pages/public/legal/Contact";
import Transparency from "@/pages/public/legal/Transparency";
import Login from "@/pages/Login";
import Dashboard from "@/pages/staff/Dashboard";
import NewCase from "@/pages/staff/NewCase";
import NewSanctuaryResident from "@/pages/staff/NewSanctuaryResident";
import CaseDetail from "@/pages/staff/CaseDetail";
import Analytics from "@/pages/admin/Analytics";
import CsvImport from "@/pages/admin/CsvImport";
import Users from "@/pages/admin/Users";
import Donations from "@/pages/admin/Donations";
import GuardianDashboard from "@/pages/admin/GuardianDashboard";
import Fundraisers from "@/pages/admin/Fundraisers";
import Diagnostics from "@/pages/admin/Diagnostics";
import Performance from "@/pages/admin/Performance";
import InventoryList from "@/pages/admin/inventory/InventoryList";
import UsageEntry from "@/pages/admin/inventory/UsageEntry";
import ItemDetail from "@/pages/admin/inventory/ItemDetail";
import ReceiveStock from "@/pages/admin/inventory/ReceiveStock";
import Reports from "@/pages/admin/inventory/Reports";
import ImportInventory from "@/pages/admin/inventory/ImportInventory";
import ImportHistory from "@/pages/admin/inventory/ImportHistory";
import InventorySettings from "@/pages/admin/inventory/InventorySettings";
import CampaignSettings from "@/pages/admin/CampaignSettings";
import DiwaliAdmin from "@/pages/admin/DiwaliAdmin";
import Diwali from "@/pages/public/Diwali";
import RouteTracker from "@/components/analytics/RouteTracker";

function AppRoutes() {
  const location = useLocation();
  // Emergent Auth returns the user here with #session_id=... in the URL
  // fragment - must be checked synchronously during render, not in an
  // effect, so AuthCallback wins the race against any other auth check.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/cases" element={<PublicCases />} />
      <Route path="/case/:slug" element={<PublicCaseDetail />} />
      <Route path="/sanctuary" element={<PublicSanctuary />} />
      <Route path="/success-stories" element={<SuccessStories />} />
      <Route path="/donate" element={<Donate />} />
      <Route path="/guardians" element={<Guardians />} />
      <Route path="/diwali" element={<Diwali />} />
      <Route path="/my-giving" element={<GuardianPortal />} />
      <Route path="/my-giving/verify" element={<GuardianPortal />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/transparency" element={<Transparency />} />
      <Route path="/login" element={<Login />} />

      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/cases/new" element={<ProtectedRoute><NewCase /></ProtectedRoute>} />
      <Route path="/sanctuary-residents/new" element={<ProtectedRoute><NewSanctuaryResident /></ProtectedRoute>} />
      <Route path="/cases/:id" element={<ProtectedRoute><CaseDetail /></ProtectedRoute>} />

      <Route path="/admin/analytics" element={<ProtectedRoute roles={["admin"]}><Analytics /></ProtectedRoute>} />
      <Route path="/admin/import" element={<ProtectedRoute roles={["admin"]}><CsvImport /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute roles={["admin"]}><Users /></ProtectedRoute>} />
      <Route path="/admin/donations" element={<ProtectedRoute roles={["admin"]}><Donations /></ProtectedRoute>} />
      <Route path="/admin/fundraisers" element={<ProtectedRoute roles={["admin"]}><Fundraisers /></ProtectedRoute>} />
      <Route path="/admin/guardians" element={<ProtectedRoute roles={["admin"]}><GuardianDashboard /></ProtectedRoute>} />
      <Route path="/admin/diagnostics" element={<ProtectedRoute roles={["admin"]}><Diagnostics /></ProtectedRoute>} />
      <Route path="/admin/performance" element={<ProtectedRoute roles={["admin"]}><Performance /></ProtectedRoute>} />
      <Route path="/admin/inventory" element={<ProtectedRoute roles={["admin", "inventory_manager"]}><InventoryList /></ProtectedRoute>} />
      <Route path="/admin/inventory/usage" element={<ProtectedRoute roles={["admin", "inventory_manager", "staff"]}><UsageEntry /></ProtectedRoute>} />
      <Route path="/admin/inventory/items/:id" element={<ProtectedRoute roles={["admin", "inventory_manager"]}><ItemDetail /></ProtectedRoute>} />
      <Route path="/admin/inventory/receive" element={<ProtectedRoute roles={["admin", "inventory_manager"]}><ReceiveStock /></ProtectedRoute>} />
      <Route path="/admin/inventory/reports" element={<ProtectedRoute roles={["admin", "inventory_manager"]}><Reports /></ProtectedRoute>} />
      <Route path="/admin/inventory/import" element={<ProtectedRoute roles={["admin"]}><ImportInventory /></ProtectedRoute>} />
      <Route path="/admin/inventory/import-history" element={<ProtectedRoute roles={["admin"]}><ImportHistory /></ProtectedRoute>} />
      <Route path="/admin/inventory/settings" element={<ProtectedRoute roles={["admin"]}><InventorySettings /></ProtectedRoute>} />
      <Route path="/admin/campaigns" element={<ProtectedRoute roles={["admin"]}><CampaignSettings /></ProtectedRoute>} />
      <Route path="/admin/diwali" element={<ProtectedRoute roles={["admin"]}><DiwaliAdmin /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <RouteTracker />
          <AppRoutes />
          <ChatWidget />
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
